import { useContext, useEffect, useState } from "react";
import { useString as s } from "../../../../components/StringProvider";
import ConnectionLayout from "../../../../components/Layout/ConnectionLayout";
import IntegrationConnectionForm from "./IntegrationConnectionForm";
import DisconnectModal from "../DisconnectModal";
import { bindActionCreators, compose } from "redux";
import { connect } from "react-redux";
import {
  selectRequestData,
  selectRequestState
} from "../../../../store/reducers";
import {
  getIntegration,
  deleteIntegration,
  setIntegration,
  connectIntegration
} from "../../../../store/actions/integrations";
import types from "../../../../store/actionTypes";
import IntegrationNames from "../../../../utils/constants/integration-names";
import useLoadingState from "../../../../utils/use-loading-state";
import { useLocation, useParams } from "react-router-dom";
import { useFeature } from "../../../../components/FeaturesProvider";
import handleOAuthRedirect from "./handle-oauth-redirect";
import { Alert, Icon } from "../../../../components";
import SalesforceInstallation from "../SalesforceInstallation";
import _ from "lodash";
import SalesforceSSOInstallation from "./SalesforceSSOInstallation";
import Notification from "../../../../components/Notification";
import { IntegrationsContext } from "../../../../providers/integrationsProvider";

const removeTrailingSlash = (url) => url?.replace(/\/$/, "");

const IntegrationConnection = ({
  setIntegrationLoadingState,
  connectIntegrationLoadingState,
  deleteIntegrationLoadingState,
  setIntegration,
  connectIntegration,
  integrationData,
  getIntegration,
  deleteIntegration,
  setIntegrationData
}) => {
  const { refreshIntegration } = useContext(IntegrationsContext);
  const { name } = useParams();
  const location = useLocation();
  const { search } = location;
  const params = new URLSearchParams(search);
  const connectionURL = removeTrailingSlash(params.get("connectionURL"));
  const integrationError = params.get("integrationError");

  const [editing, setEditing] = useState(false);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const salesforceIntegrationV2 = useFeature("salesforceIntegrationV2", false);
  const salesforceSandbox = useFeature("salesforceSandbox", false);
  const isCuvamaDiscovery = name === IntegrationNames.CUVAMA_DISCOVERY;
  const [formFields, setFormFields] = useState([
    "baseUrl",
    "clientId",
    "clientSecret"
  ]);

  const setIntegrationError = s(
    "generalAdmin.page.integrationConnection.messages.setIntegrationError",
    "Failed to update connection information"
  );
  const errorTitle = s(
    "generalAdmin.page.integrationConnection.messages.integrationError",
    "Integration Error"
  );

  const integrationNames = {
    hubspot: s("integrationNames.hubspot", "HubSpot"),
    salesforce: s("integrationNames.salesforce", "Salesforce"),
    "cuvama-discovery": s(
      "integrationNames.cuvamaDiscovery",
      "Cuvama Discovery"
    )
  };
  const integrationName = integrationNames[name];
  const integrationErrorMessages = {
    "error.retrieveIntegrationToken.generalError": s(
      "error.retrieveIntegrationToken.generalError",
      "Unfortunately, we weren't able to connect to {integrationName}. Please check your settings and try again.",
      { integrationName }
    ),
    "error.retrieveIntegrationToken.pingFailed": s(
      "error.retrieveIntegrationToken.pingFailed",
      "Unfortunately, we weren't able to connect to {integrationName}. Please check your settings and try again.",
      { integrationName }
    ),
    "error.connectIntegration.connectionTestFailed": s(
      "error.connectIntegration.connectionTestFailed",
      "Unfortunately, we weren't able to connect to {integrationName}. Please check your API Key and Organization ID and try again.",
      { integrationName }
    )
  };
  const helperText = s(
    "generalAdmin.page.integrationConnection.header",
    "Enter the connection details you generated in {integrationName} to activate the connection between Cuvama and {integrationName}.",
    { integrationName }
  );
  const disconnectHeader = s(
    "disconnectIntegration.popup.header",
    "Disconnect {integrationName}",
    { integrationName }
  );
  const disconnectSubheader = s(
    "disconnectIntegration.popup.subheader",
    "Do you want to disconnect from {integrationName} and delete your settings?",
    { integrationName }
  );

  const salesforceConnectionTitle = s(
    "generalAdmin.page.salesforceConnection.title",
    "Salesforce Connection"
  );

  const title =
    name === IntegrationNames.SALESFORCE && salesforceIntegrationV2
      ? salesforceConnectionTitle
      : undefined;

  useEffect(() => {
    if (name === IntegrationNames.HUBSPOT) {
      setFormFields([]);
    } else if (
      name === IntegrationNames.SALESFORCE &&
      salesforceIntegrationV2
    ) {
      setFormFields(["baseUrl"]);
    } else if (name === IntegrationNames.CUVAMA_DISCOVERY) {
      setFormFields(["apiKey", "orgId"]);
    }
  }, [salesforceIntegrationV2, name]);

  useEffect(() => {
    getIntegration({ name });
    refreshIntegration(name);
  }, [name]);

  useEffect(() => {
    if (
      !_.isEqual({}, integrationData) &&
      connectionURL &&
      name === IntegrationNames.SALESFORCE &&
      salesforceIntegrationV2
    ) {
      deleteIntegration({ name });
    }

    if (
      connectionURL &&
      name === IntegrationNames.SALESFORCE &&
      salesforceIntegrationV2
    ) {
      setEditing(true);
    } else {
      setEditing(!integrationData);
    }
  }, [integrationData]);

  useLoadingState(
    setIntegrationLoadingState,
    () => {
      if (!isCuvamaDiscovery) {
        handleOAuthRedirect({
          salesforceIntegrationV2,
          salesforceSandbox,
          name,
          ...setIntegrationData
        });
      }
    },
    () => {
      Notification.error(setIntegrationError);
    }
  );

  const connectSuccessMessage = s(
    "generalAdmin.page.integrationConnection.messages.connectSuccess",
    "Successfully connected to {integrationName}",
    { integrationName }
  );
  const connectErrorMessage = s(
    "generalAdmin.page.integrationConnection.messages.connectError",
    "Failed to connect to {integrationName}. Please check your credentials and try again.",
    { integrationName }
  );

  useLoadingState(
    connectIntegrationLoadingState,
    () => {
      getIntegration({ name });
      refreshIntegration(name);
      setEditing(false);
    },
    () => {
      Notification.error(connectErrorMessage);
    }
  );

  const onConnect = (formData) => {
    if (isCuvamaDiscovery) {
      connectIntegration({
        name,
        apiKey: formData.apiKey,
        settings: { orgId: formData.orgId }
      });
    } else {
      setIntegration({
        name,
        baseUrl: formData.baseUrl,
        clientId: formData.clientId,
        clientSecret: formData.clientSecret
      });
    }
  };

  const onUpdate = (formData) => {
    if (isCuvamaDiscovery) {
      connectIntegration({
        name,
        ...(formData.apiKey ? { apiKey: formData.apiKey } : {}),
        settings: { orgId: formData.orgId }
      });
    } else {
      setIntegration({
        name,
        baseUrl: formData.baseUrl,
        clientId: formData.clientId,
        clientSecret: formData.clientSecret
      });
    }
  };

  const onEdit = () => {
    setEditing(true);
  };

  const onCancelEdit = () => {
    setEditing(false);
  };

  const onDisconnect = () => {
    setDeleteModalVisible(true);
  };

  const onDelete = () => {
    deleteIntegration({ name });
    refreshIntegration(name);
  };

  const getIntegrationData = () => {
    let data = integrationData;

    if (connectionURL) {
      data = { ...data, baseUrl: connectionURL };
    }

    if (data?.settings?.orgId) {
      data = { ...data, orgId: data.settings.orgId };
    }

    return data;
  };

  return (
    <ConnectionLayout
      title={title}
      helperText={helperText}
      isConnected={!!integrationData?.connected}
      key={name}
    >
      <IntegrationConnectionForm
        formFields={formFields}
        integrationName={integrationNames[name]}
        integrationData={getIntegrationData()}
        onConnect={onConnect}
        onCancel={onCancelEdit}
        onDisconnect={onDisconnect}
        onEdit={onEdit}
        onUpdate={onUpdate}
        readOnly={!editing}
        isConnected={!!integrationData?.connected}
      />
      {deleteModalVisible && (
        <DisconnectModal
          onClose={() => setDeleteModalVisible(false)}
          deleteLoadingState={deleteIntegrationLoadingState}
          header={disconnectHeader}
          subHeader={disconnectSubheader}
          handleDelete={onDelete}
        />
      )}
      {integrationError && !editing && (
        <Alert
          data-cy={"integration-error"}
          message={errorTitle}
          description={
            integrationErrorMessages[integrationError] ||
            integrationErrorMessages[
              "error.retrieveIntegrationToken.generalError"
            ]
          }
          type={"error"}
          icon={<Icon size={"larger"} name={"alert"} />}
          showIcon
        />
      )}

      {name === IntegrationNames.SALESFORCE && salesforceIntegrationV2 && (
        <SalesforceInstallation />
      )}

      {name === IntegrationNames.SALESFORCE && salesforceIntegrationV2 && (
        <SalesforceSSOInstallation />
      )}
    </ConnectionLayout>
  );
};

const mapStateToProps = (state) => ({
  deleteIntegrationLoadingState: selectRequestState(
    state,
    types.DELETE_INTEGRATION_REQUEST
  ),
  setIntegrationLoadingState: selectRequestState(
    state,
    types.SET_INTEGRATION_REQUEST
  ),
  connectIntegrationLoadingState: selectRequestState(
    state,
    types.CONNECT_INTEGRATION_REQUEST
  ),
  integrationData:
    selectRequestData(state, types.GET_INTEGRATION_REQUEST) || {},
  setIntegrationData: selectRequestData(state, types.SET_INTEGRATION_REQUEST)
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    { setIntegration, getIntegration, deleteIntegration, connectIntegration },
    dispatch
  );

export default compose(connect(mapStateToProps, mapDispatchToProps))(
  IntegrationConnection
);
